import React, { ButtonHTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';
import { Theme } from '~types/types';

interface ButtonProps {
  primary?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
  danger?: boolean;
  small?: boolean;
  large?: boolean;
  disabled?: boolean;
  link?: string;
  onClick?: any;
  type?: 'button' | 'submit' | 'reset';
  children?: ReactNode;
  className?: string;
}

interface StyledButtonProps {
  $primary?: boolean;
  $secondary?: boolean;
  $tertiary?: boolean;
  $danger?: boolean;
  $small?: boolean;
  $large?: boolean;
  $link?: string;
  disabled?: boolean;
  theme?: Theme;
}

const Btn = styled.button<StyledButtonProps>`
  border: none;
  border-radius: ${props => props.theme.baseRadius};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  display: inline-block;
  font-size: ${props => {
    const { buttonFontSize } = props.theme;
    const buttonFontSizeParsed = parseInt(buttonFontSize, 10);
    return (
      (props.$small && `${buttonFontSizeParsed * 0.875}px`) ||
      (props.$large && `${buttonFontSizeParsed * 1.375}px`) ||
      buttonFontSize
    );
  }};
  font-weight: ${props => props.theme.fontSemibold};
  line-height: ${props => (props.$small && '1') || (props.$large && '1.25') || '1.2'};
  padding: ${props => (props.$large ? '16px 25px 17px' : '9px 12px')};
  position: relative;
  text-align: center;
  color: ${props => (props.$link && props.theme.baseFontColor) || '#fff'};
  background-color: ${props =>
    (props.$primary && props.theme.primary) ||
    (props.$secondary && props.theme.secondary) ||
    (props.$tertiary && props.theme.tertiary) ||
    (props.$danger && props.theme.danger) ||
    (props.$link && '#fff')};
  text-transform: uppercase;
  opacity: ${props => (props.disabled && '0.5') || '1'};
  transition: all 0.2s;

  &:hover {
    ${props => props.$link && !props.disabled && 'text-decoration: underline;'}
    opacity: ${props => !props.$link && !props.disabled && '0.75'}
  }

  &:active {
    box-shadow: ${props => !props.$link && 'inset 3px 3px 10px rgba(0,0,0,0.3)'}
  }

  svg {
    fill: ${props => (props.$link && props.theme.baseFontColor) || '#fff'};
    max-width: ${props => {
      const { buttonFontSize } = props.theme;
      const buttonFontSizeParsed = parseInt(buttonFontSize, 10);
      return (
        (props.$small && `${buttonFontSizeParsed * 0.875}px`) ||
        (props.$large && `${buttonFontSizeParsed * 1.375}px`) ||
        buttonFontSize
      );
    }};
    max-height: ${props => {
      const { buttonFontSize } = props.theme;
      const buttonFontSizeParsed = parseInt(buttonFontSize, 10);
      return (
        (props.$small && `${buttonFontSizeParsed * 0.875}px`) ||
        (props.$large && `${buttonFontSizeParsed * 1.375}px`) ||
        buttonFontSize
      );
    }};
    margin-right: 10px;
    position: relative;
    top: 2px;
  }
`;

export const Button: React.FC<ButtonProps> = ({
  primary,
  secondary,
  tertiary,
  danger,
  small,
  large,
  link,
  disabled,
  onClick,
  type,
  children,
  className,
}) => {
  return (
    <Btn
      $primary={primary}
      $secondary={secondary}
      $tertiary={tertiary}
      $danger={danger}
      $small={small}
      $large={large}
      $link={link}
      disabled={disabled}
      onClick={onClick}
      type={type}
      className={className}
    >
      {children}
    </Btn>
  );
};

export default Button;
